import { NodeTypes } from '@cloudike/web_files'
import _ from "lodash"
import moment from 'moment'

import { request } from "../api/request"

interface Node {
  id: string;
  name: string;
  type: string;
  parent_id?: string;
  children?: Node[];
}


export const generateKey = (prefix: string | number) => {
  return `${ prefix }_${ new Date().getTime() }`
}

export const toMb = (size: number) => {
  return (size / 1024 / 1024).toFixed(2) + ' MB'
}

export const toGb = (size: number) => {
  return Number((parseInt((size / 1024 / 1024 / 1024).toFixed(1))) % 1 === 0 ? (size / 1024 / 1024 / 1024).toFixed(2) : (size / 1024 / 1024 / 1024).toFixed(2))
}

export const downloadItemByLink = async (link: string) => {
  const {
    _links: {
      data: { href }
    }
  } = await request<{ _links: { data: { href: string } } }>('GET', link, {}, { host: null })

  return href
}

export const getErrorByFieldName = (error: any, fieldName: string): any => {
  if (_.has(error, 'cause')) {
    return getErrorByFieldName(error.cause, fieldName)
  }
  return error[fieldName]
}

export const getHighestLevelErrorByFieldName = (error: any, fieldName: string): any => {
  if (error[fieldName]) {
    return error[fieldName]
  }

  if (_.has(error, 'cause')) {
    return getHighestLevelErrorByFieldName(error.cause, fieldName)
  }
}



export function getTreeLikeMenuFromNodes(nodes: Node[], onClick: (item: Node) => void): Node[] {
  const mapExtraFields = (node: Node) => {
    return {
      ...node,
      label: node.name,
      key: node.id,
      onTitleClick: () => {
        onClick(node)
      },
    }
  }

  return getTreeLikeDataFromNodes(nodes, mapExtraFields)
}

export const getDisabledTreeData = (nodes: any[], selectedItemsIds: string[]) => {
  const idSet = new Set(selectedItemsIds)

  const disableAllChildren = (node: any): any => ({
    ...node,
    disabled: true,
    children: node.children ? node.children.map(disableAllChildren) : []
  })

  const processNode = (node: any, parent: any = null): any => {
    const isNodeSelected = idSet.has(node.key)
    const hasSelectedChild = node.children?.some((child: any) => idSet.has(child.key))

    const disabled = isNodeSelected || hasSelectedChild || (parent && idSet.has(parent.key))

    let children = node.children
    if (isNodeSelected && node.children) {
      children = node.children.map(disableAllChildren)
    } else if (node.children) {
      children = node.children.map(child => processNode(child, node))
    }

    return { ...node, disabled, children }
  }

  return nodes.map(node => processNode(node))
}


export function getTreeLikeSelectorFromNodes(nodes: Node[], selectedItemsIds: string[]): Node[] {
  const mapExtraFields = (node: Node) => ({
    ...node,
    title: node.name,
    key: node.id,
  })

  const treeData = getTreeLikeDataFromNodes(nodes, mapExtraFields)
  return getDisabledTreeData(treeData, selectedItemsIds)
}

function removeEmptyChildrenFromTree(node: Node) {
  if (node.children) {
    node.children = node.children.filter(Boolean)
    if (node.children.length === 0) {
      delete node.children
    } else {
      node.children.forEach(removeEmptyChildrenFromTree)
    }
  }
}

export function getTreeLikeDataFromNodes(nodes: Node[], mapExtraFields: (node: Node) => any): Node[] {
  const nodeMap = new Map<string, Node>()
  nodes.forEach(node => {
    const extraNode = mapExtraFields(node)
    nodeMap.set(node.id, node.type === NodeTypes.DIR ? { ...extraNode, children: [] } : { ...extraNode })
  })

  const rootNode = nodes.find(node => node.id === 'root')
  if (rootNode) {
    const root = nodeMap.get('root')!
    // Прямые дети root — parent_id === '' и не root
    nodes.forEach(node => {
      if (node.parent_id === '' && node.id !== 'root') {
        root.children!.push(nodeMap.get(node.id)!)
      }
    })
    // Остальная иерархия
    nodes.forEach(node => {
      if (
        node.id !== 'root' &&
        node.parent_id &&
        node.parent_id !== '' &&
        nodeMap.has(node.parent_id)
      ) {
        const currentNode = nodeMap.get(node.id)!
        const parent = nodeMap.get(node.parent_id)!
        parent.children = parent.children || []
        parent.children.push(currentNode)
      }
    })
    removeEmptyChildrenFromTree(root)
    return [root]
  }

  // Обычная иерархия, если root нет
  const result: Node[] = []
  nodes.forEach(node => {
    const currentNode = nodeMap.get(node.id)!
    if (node.parent_id && nodeMap.has(node.parent_id)) {
      const parent = nodeMap.get(node.parent_id)!
      parent.children = parent.children || []
      parent.children.push(currentNode)
    } else {
      result.push(currentNode)
    }
  })
  result.forEach(removeEmptyChildrenFromTree)
  return result
}

export function calculateSize(bytes: number) {
  const sizeName = ['bytes', 'KB', 'MB', 'GB', 'TB']

  const number = bytes ? Math.floor(Math.log(bytes) / Math.log(1024)) : 0
  const size =
    Math.trunc(bytes / Math.pow(1024, number)) + ' ' + sizeName[number]

  return size
}

const extractPreviewPayload = (item: any) => {
  const directPreview = item?._embedded?.previews

  if (directPreview?.sizes) {
    return directPreview
  }

  const versions = item?._embedded?.versions
  if (!Array.isArray(versions) || versions.length === 0) {
    return null
  }

  const currentVersion = versions.find((version) => version?.is_current) || versions[0]
  const versionPreview = currentVersion?._embedded?.previews

  if (versionPreview?.sizes) {
    return versionPreview
  }

  return null
}

const buildPreviewUrl = (previews: any, size: string) => {
  if (!previews?.sizes?.[size]) {
    return ''
  }

  const urlTemplate = _.template(previews.url_template, { interpolate: /\{(.+?)\}/g })
  return `${urlTemplate({ size_name: size })}&jwt=${previews.jwt}`
}

export function getSmallPreviewLink(item) {
  const previews = extractPreviewPayload(item)
  return buildPreviewUrl(previews, 'small')
}

export function getMiddlePreviewLink(item) {
  const previews = extractPreviewPayload(item)
  return buildPreviewUrl(previews, 'middle')
}

export function getPreviewLink(item) {
  const previews = extractPreviewPayload(item)
  return buildPreviewUrl(previews, 'preview')
}

export function getAttachmentPreviewLink(attachment) {
  if (!attachment?._embedded?.previews?.sizes?.preview) {
    return ''
  }

  const urlTemplate = _.template(attachment._embedded.previews.url_template, { interpolate: /\{(.+?)\}/g })
  const url = `${urlTemplate({ size_name: 'preview' })}&jwt=${attachment._embedded.previews.jwt}`

  return url
}

export function calculateItemSize(file) {
  const bytes = file?.file_info?.size || file?.size
  return calculateSize(bytes)
}

export const mapFileItemsToTableRow = (items) => {
  return items.map((item) => {
    let icon
    const date = moment(item.created).utc(true).format('MMM DD, yyyy, HH:mm')


    if (item?.file_info?.type !== null) {
      switch (item?.file_info?.type) {
      case 'image':
        icon = 'file_image'
        break
      case 'video':
        icon = 'file_video'
        break
      case 'audio':
        icon = 'file_audio'
        break
      }
    }

    return {
      ...item,
      name: item.name,
      shared: item.is_shared,
      modified: date,
      size: calculateItemSize(item),
      access: item.is_shared ? 'Shared' : 'Only you',
      icon: icon,
      id: item.id,
      thumbnailLink: item?.file_info?.type !== 'audio' ? getSmallPreviewLink(item) : null
    }
  })
}

export function generateFilesItemName(
  itemName,
  justNeedToAddSuffix = false,
  isFolder = false
) {
  const spacer = ' '
  const startValue = 1
  const regExp = /\((\d+?)\)$/

  let newName = ''
  const dot = '.'
  let count = startValue

  const splittedByDot = itemName.split(dot)

  const extension = isFolder ? '' : splittedByDot.length > 1 ? splittedByDot.slice(-1) : ''
  const name = isFolder ? splittedByDot.join('.') : splittedByDot.slice(0, extension ? splittedByDot.length - 1 : 1).join('.')

  const matches = name.match(regExp)

  const tail = `${dot}${extension}`

  if (matches && !justNeedToAddSuffix) {
    const [, countStr] = matches
    if (countStr) {
      count = parseInt(countStr)
      count++
    }
    newName = name.replace(regExp, `(${count})`)
  } else {
    newName = `${name}${spacer}(${count})`
  }

  if (extension) {
    newName += tail
  }

  return newName
}

export function getMediaTypeFromVersion(versionData: any): string {
  if (!versionData) return 'unknown'

  return versionData.file_info?.type || 'unknown'
}

export function getMimeTypeFromVersion(versionData: any): string {
  if (!versionData) return ''

  return versionData.file_info?.mime || ''
}

export function isVideoType(type: string): boolean {
  return type === 'video'
}

export function isImageType(type: string): boolean {
  return type === 'image' || type === 'vector'
}

export function isAudioType(type: string): boolean {
  return type === 'audio'
}

export function isPdfType(mimeType: string): boolean {
  return mimeType === 'application/pdf'
}

export function hasPdfPreview(item: any): boolean {
  return !!item?._embedded?.previews?.sizes?.preview &&
         item?._embedded?.previews?.sizes?.preview?.type === 'pdf'
}

export function getMediaViewerType(versionData: any): 'image' | 'video' | 'audio' | 'pdf' | 'unknown' {

  if (!versionData) return 'unknown'

  const mediaType = getMediaTypeFromVersion(versionData)
  const mimeType = getMimeTypeFromVersion(versionData)

  if (isVideoType(mediaType)) return 'video'
  if (isImageType(mediaType)) return 'image'
  if (isAudioType(mediaType)) return 'audio'
  if (isPdfType(mimeType) || hasPdfPreview(versionData)) return 'pdf'

  return 'unknown'
}

export function getContentLink(item: any): string {
  if (!item?._links?.content?.href) {
    return ''
  }
  return item._links.content.href
}

export function hasContentLink(item: any): boolean {
  return !!item?._links?.content?.href
}
